/**
 * Профайлер для ServiceKernelLoader
 */

import { ApiOutlined, EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { App, Button, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd'
import React, { useMemo, useState } from 'react'
import { Kernel } from '../../../Kernel'
import { DebugProfilerData, DebugProfilerInterface } from '../types'
import { JsonViewer } from '../components/JsonViewer'

const { Text } = Typography

// Компонент для відображення деталей сервісу
const ServiceDetailsModal: React.FC<{ service: any; serviceName: string }> = ({ service, serviceName }) => {
    const [isVisible, setIsVisible] = useState(false)
    const { message } = App.useApp()

    const serviceInfo = useMemo(() => {
        const type = typeof service
        const isClass = service?.constructor?.name && service.constructor.name !== 'Object'
        const methods = []
        const properties = []

        if (service && typeof service === 'object') {
            for (const key in service) {
                if (typeof service[key] === 'function') {
                    methods.push(key)
                } else {
                    properties.push(key)
                }
            }
        }

        return {
            type,
            isClass,
            className: isClass ? service.constructor.name : 'N/A',
            methods: methods.sort(),
            properties: properties.sort(),
            service,
        }
    }, [service])

    const copyToClipboard = () => {
        const jsonStr = JSON.stringify(serviceInfo, null, 2)
        navigator.clipboard
            .writeText(jsonStr)
            .then(() => {
                message.success('JSON скопійовано!')
            })
            .catch((err) => {
                console.error('Помилка копіювання:', err)
            })
    }

    return (
        <>
            <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setIsVisible(true)}
                style={{ padding: 0, height: 'auto' }}
            >
                Деталі
            </Button>
            <Modal
                title={`Сервіс: ${serviceName}`}
                open={isVisible}
                onCancel={() => setIsVisible(false)}
                footer={[
                    <Button key="copy" onClick={copyToClipboard}>
                        Копіювати
                    </Button>,
                    <Button key="close" type="primary" onClick={() => setIsVisible(false)}>
                        Закрити
                    </Button>,
                ]}
                width={900}
                styles={{ body: { padding: '16px' } }}
            >
                <JsonViewer data={serviceInfo} showSearch={true} />
            </Modal>
        </>
    )
}

// Компонент для відображення таблиці сервісів
const ServicesTable: React.FC<{ services: Record<string, any>; onRefresh?: () => void }> = ({
    services,
    onRefresh,
}) => {
    const [searchText, setSearchText] = useState('')
    const [sourceFilter, setSourceFilter] = useState<string | undefined>(undefined)

    // Перетворюємо сервіси в масив для відображення
    const servicesArray = useMemo(() => {
        return Object.entries(services).map(([key, service]) => {
            const isClass = service?.constructor?.name && service.constructor.name !== 'Object'
            const type = typeof service
            const className = isClass ? service.constructor.name : 'N/A'
            
            // Визначаємо джерело (Core, Ship, Container)
            let source = 'Core'
            if (key.includes('.')) {
                const parts = key.split('.')
                if (parts[0] === 'Ship') {
                    source = 'Ship'
                } else {
                    source = 'Container'
                }
            }

            // Підраховуємо методи
            let methodsCount = 0
            if (service && typeof service === 'object') {
                for (const prop in service) {
                    if (typeof service[prop] === 'function') {
                        methodsCount++
                    }
                }
            }

            return {
                key,
                name: key,
                shortName: key.split('.').pop() || key,
                type,
                className,
                isClass,
                methodsCount,
                source,
                service,
            }
        })
    }, [services])

    // Отримуємо унікальні значення для фільтрів
    const sources = useMemo(() => {
        const uniqueSources = [...new Set(servicesArray.map((s) => s.source))]
        return uniqueSources.sort()
    }, [servicesArray])

    // Фільтрація даних
    const filteredServices = useMemo(() => {
        return servicesArray.filter((service) => {
            // Пошук по підстроці
            const searchMatch =
                searchText === '' ||
                service.name.toLowerCase().includes(searchText.toLowerCase()) ||
                service.shortName.toLowerCase().includes(searchText.toLowerCase()) ||
                service.className.toLowerCase().includes(searchText.toLowerCase())

            // Фільтр за джерелом
            const sourceMatch = sourceFilter === undefined || service.source === sourceFilter

            return searchMatch && sourceMatch
        })
    }, [servicesArray, searchText, sourceFilter])

    const columns = [
        {
            title: 'Назва сервісу',
            dataIndex: 'name',
            key: 'name',
            width: '35%',
            render: (name: string) => (
                <Text code style={{ fontSize: '12px' }}>
                    {name}
                </Text>
            ),
        },
        {
            title: 'Клас',
            dataIndex: 'className',
            key: 'className',
            width: '30%',
            render: (className: string, record: any) => (
                <Tag color={record.isClass ? 'blue' : 'default'}>{className}</Tag>
            ),
        },
        {
            title: 'Джерело',
            dataIndex: 'source',
            key: 'source',
            width: '20%',
            filters: sources.map((s) => ({ text: s, value: s })),
            onFilter: (value: any, record: any) => record.source === value,
            render: (source: string) => {
                const color = source === 'Core' ? 'blue' : source === 'Ship' ? 'green' : 'orange'
                return <Tag color={color}>{source}</Tag>
            },
        },
        {
            title: 'Дії',
            key: 'actions',
            width: '15%',
            render: (_: any, record: any) => (
                <ServiceDetailsModal service={record.service} serviceName={record.name} />
            ),
        },
    ]

    return (
        <Space orientation="vertical" style={{ width: '100%' }}>
            {/* Фільтри */}
            <Space wrap>
                <Input
                    placeholder="Пошук за назвою або класом..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                    allowClear
                />
                <Select
                    placeholder="Джерело"
                    value={sourceFilter}
                    onChange={setSourceFilter}
                    style={{ width: 150 }}
                    allowClear
                >
                    {sources.map((source) => (
                        <Select.Option key={source} value={source}>
                            {source}
                        </Select.Option>
                    ))}
                </Select>
                {onRefresh && (
                    <Button icon={<ReloadOutlined />} onClick={onRefresh}>
                        Оновити
                    </Button>
                )}
            </Space>

            {/* Таблиця */}
            <Table
                columns={columns}
                dataSource={filteredServices}
                rowKey="name"
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Всього ${total} сервісів`,
                }}
                size="small"
                scroll={{ x: 'max-content' }}
            />
        </Space>
    )
}

export class ServiceLoaderProfiler implements DebugProfilerInterface {
    key = 'service-loader'
    name = 'Сервіси'
    icon = <ApiOutlined />
    priority = 70

    isEnabled(): boolean {
        return true
    }

    async getData(onRefresh?: () => void): Promise<DebugProfilerData> {
        try {
            const kernel = Kernel.getInstance()
            const serviceLoader = kernel.getLoader('service') as any

            if (!serviceLoader) {
                return {
                    summary: {
                        count: 0,
                        status: 'warning',
                        label: 'Нет данных',
                    },
                    details: {
                        custom: <div>ServiceLoader не найден</div>,
                    },
                }
            }

            const serviceNames = serviceLoader.getServiceNames ? serviceLoader.getServiceNames() : []

            if (serviceNames.length === 0) {
                return {
                    summary: {
                        count: 0,
                        status: 'warning',
                        label: 'Нет сервисов',
                    },
                    details: {
                        custom: <div>Сервисы не загружены</div>,
                    },
                }
            }

            // Отримуємо всі сервіси
            const services: Record<string, any> = {}
            serviceNames.forEach((name: string) => {
                const service = serviceLoader.getService(name)
                if (service) {
                    services[name] = service
                }
            })

            return {
                summary: {
                    count: serviceNames.length,
                    status: serviceNames.length > 0 ? 'success' : 'warning',
                    label: `${serviceNames.length} сервісів`,
                },
                details: {
                    custom: onRefresh ? (
                        <ServicesTable services={services} onRefresh={onRefresh} />
                    ) : (
                        <ServicesTable services={services} />
                    ),
                },
            }
        } catch (error) {
            console.error('Ошибка получения данных сервисов:', error)

            return {
                summary: {
                    count: 0,
                    status: 'error',
                    label: 'Ошибка',
                },
                details: {
                    custom: (
                        <div style={{ color: '#ff4d4f' }}>
                            {error instanceof Error ? error.message : 'Неизвестная ошибка'}
                        </div>
                    ),
                },
            }
        }
    }
}
